import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { getFadeColor } from '@repo/toolkit/colors'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { PlusIcon, TravelTwoIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import React, { useState } from 'react'

import { createJourney } from '../../../../api/products/create-journey'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { useScrollTo } from '../../../../lib/hooks/use-scroll-to'
import { useSearchParams } from '../../../../lib/hooks/use-search-params'
import { useProductStore } from '../../../../lib/stores/use-product'
import {
  ProductDate as ProductDateType,
  ProductDateProperties
} from '../../../../lib/types/models/product/product-date'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import JourneyForm from '../journey-form'
import { ChevronDownContainer } from './common'
import ProductDateHeader from './product-date-header'
import ProductDateJourney from './product-date-journey'

type Props = {
  productDate: ProductDateType
}

type SearchParams = {
  'scroll-to': string
}

const ProductDate: React.FC<Props> = ({ productDate }) => {
  const { searchParams } = useSearchParams<SearchParams>()
  const { addJourney } = useProductStore()
  const { modalState, toggleModal } = useModalManager<'create-journey'>(['create-journey'])
  const [open, setOpen] = useState(false)

  const { mutateAsync: createJourneyMutation } = useMutation({
    mutationFn: createJourney,
    onError: showAxiosError,
    onSuccess: (data) => {
      addJourney(productDate.id, data)
    }
  })

  useScrollTo(searchParams['scroll-to'])

  return (
    <div className="p-5 shadow rounded-xl">
      <ProductDateHeader productDate={productDate} />
      <div className="h-5" />
      <div className="flex justify-between">
        <div className="flex items-center gap-2">
          <ChevronDownContainer
            open={open}
            color={
              !productDate.journeys.length
                ? tailwindColors.grey.light.DEFAULT
                : tailwindColors.primary.DEFAULT
            }
            className={cn('bg-white-active', {
              'cursor-not-allowed bg-grey-stroke': !productDate.journeys.length
            })}
            onClick={() => {
              if (productDate.journeys.length > 0) setOpen((prev) => !prev)
            }}
          />
          <Typography variant="sub-heading">Séjours liés à cette date</Typography>
        </div>
        <Button
          disabled={productDate.locked}
          variant="primary"
          label="Ajouter un séjour"
          icon={PlusIcon}
          onClick={() => toggleModal('create-journey')}
        />
      </div>
      <div className="h-5" />
      {open &&
        productDate.journeys.map((journey) => (
          <div id={journey.id.toString()} className="mb-3" key={journey.id}>
            <ProductDateJourney journey={journey} locked={productDate.locked} />
          </div>
        ))}
      <Modal
        isOpen={modalState['create-journey']}
        title="Ajouter un séjour du"
        headerChildren={
          <Badge
            text={`${DateFormatter.DDMMYYYY(productDate.departureDate)} - ${DateFormatter.DDMMYYYY(productDate.returnDate)}`}
            variant="grey"
            textColor={ProductDateProperties.getColor(productDate)}
            color={getFadeColor(ProductDateProperties.getColor(productDate))}
            size="small"
            textVariant="caption"
          />
        }
        icon={TravelTwoIcon}
        size={'medium'}
        hideActions
        onCancel={() => toggleModal('create-journey')}
      >
        <JourneyForm
          productDateId={productDate.id}
          periodName={productDate.period?.name}
          className={MODAL_BODY_CLASSNAME}
          defaultValues={{
            fa1eIncluded: false,
            fa2eIncluded: false,
            isMandatoryUnderAge: false,
            secondaryOptions: []
          }}
          buttonsConfig={modalButtonConfig({
            onCancel: () => toggleModal('create-journey'),
            onConfirm: async ({ isMandatoryUnderAge: _isMandatoryUnderAge, ...values }) => {
              await createJourneyMutation({ ...values, productDateId: productDate.id })
              toggleModal('create-journey')
            }
          })}
        />
      </Modal>
    </div>
  )
}

export default ProductDate
